import type { FlowRun, FlowRunNode, FlowTriggerNodeConfig } from '@reflex/flows';
import { rendererKeyForTrigger } from '../../shared/event-catalog';
import { summarizeTriggerNode } from '../../shared/trigger-summary';

// Generic payload-formatting helpers now live in the shared design system so
// other plugins (e.g. plugin-chat) can render structured payloads the same
// way. Re-exported here to keep existing plugin-flows imports stable.
export {
  formatLabel,
  isMarkdownLike,
  parseJsonPayload,
} from '@reflex/ui/components/content/StructuredPayloadView';

export type FlowRunChatBubble =
  | {
      kind: 'trigger';
      key: string;
      run: FlowRun;
      /** Renderer-registry lookup keys for the fired trigger, most specific first. */
      triggerKeys: string[];
      /** Human label for the fired trigger (e.g. `slack · slack_message`). */
      triggerLabel: string;
      /** Config of the fired trigger node, when known. Lets the view render
       *  kind-specific layouts (e.g. service-run attribution). */
      triggerConfig: FlowTriggerNodeConfig | undefined;
    }
  | { kind: 'node'; key: string; node: FlowRunNode };

interface BuildChatBubblesArgs {
  run: FlowRun;
  nodes: readonly FlowRunNode[] | undefined;
  /** Config of the trigger node the run was anchored at, when known. */
  triggerConfig: FlowTriggerNodeConfig | undefined;
}

/**
 * Build the ordered list of bubbles rendered by `FlowRunChatView`.
 *
 * - Leading `trigger` bubble summarises the flow's trigger source and
 *   `run.triggerData` payload.
 * - One `node` bubble per `FlowRunNode`, sorted by `startedAt ?? run.createdAt`
 *   and de-duplicated by `${nodeId}:${iteration}` (the flow engine may
 *   temporarily emit two rows for a looping node during a retry).
 */
export function buildChatBubbles({
  run,
  nodes,
  triggerConfig,
}: BuildChatBubblesArgs): FlowRunChatBubble[] {
  const bubbles: FlowRunChatBubble[] = [
    {
      kind: 'trigger',
      key: `trigger:${run.id}`,
      run,
      triggerKeys: triggerConfig ? rendererKeyForTrigger(triggerConfig) : ['manual'],
      triggerLabel: triggerConfig ? summarizeTriggerNode({ config: triggerConfig }) : 'Manual',
      triggerConfig,
    },
  ];

  const seen = new Set<string>();
  const sorted = [...(nodes ?? [])]
    .filter((n) => n.status !== 'skipped')
    .filter((n) => n.status !== 'pending' || n.startedAt != null)
    .sort((a, b) => {
      const aTs = a.startedAt ?? run.createdAt;
      const bTs = b.startedAt ?? run.createdAt;
      return aTs - bTs;
    });

  for (const node of sorted) {
    const key = `${node.nodeId}:${node.iteration}`;
    if (seen.has(key)) continue;
    seen.add(key);
    bubbles.push({ kind: 'node', key, node });
  }

  return bubbles;
}

/**
 * Extract a short, human-readable summary from an LLM node's `output` record.
 * Prefers well-known keys, falls back to the first string value, then to
 * a JSON stringification.
 */
export function summariseLlmOutput(
  output: Record<string, unknown> | null | undefined,
  maxLength = 400,
): string | null {
  if (!output) return null;

  const preferredKeys = ['text', 'llmResult', 'result', 'summary', 'response', 'content'];
  for (const key of preferredKeys) {
    const value = output[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return truncate(value, maxLength);
    }
  }

  for (const value of Object.values(output)) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return truncate(value, maxLength);
    }
  }

  try {
    return truncate(JSON.stringify(output), maxLength);
  } catch {
    return null;
  }
}

function truncate(input: string, maxLength: number): string {
  if (input.length <= maxLength) return input;
  return `${input.slice(0, maxLength).trimEnd()}…`;
}

/**
 * Resolve the structured payload for a node output, given an optional
 * `outputKey` declared on the node config (LLM / agent_task). Falls back to
 * the raw output when the keyed value is absent or not an object/string.
 */
export function resolveStructuredPayload(
  output: Record<string, unknown> | null | undefined,
  outputKey: string | undefined,
): unknown {
  if (!output) return null;
  if (outputKey && outputKey in output) {
    return output[outputKey];
  }
  return output;
}
